/**
 * .Bank Cumulative Report — shared statistics computation.
 *
 * Pure functions (no Convex imports) used by both the report page (live
 * values for the current period) and the month-end snapshot cron, so the two
 * always agree on how a metric is derived.
 */

import type { getRegion } from '../src/lib/region-utils';

export interface LiveDomainRow {
  domain: string;
  effectiveLive: boolean;
  registrationDate: number | null;
  liveSince: number | null;
  registrantEmail: string | null;
  registrantRegion: ReturnType<typeof getRegion>;
}

/** The subset of a deal that the stats need. Structurally satisfied by the API's Deal type. */
export interface StatsDeal {
  deal_type: string;
  domains: string[];
  created_at: string;
  outcome: string | null;
}

/**
 * The reporting window. `monthStart`/`yearStart` are inclusive epoch millis;
 * `end` is exclusive. For a live view `end` is +Infinity; for a backdated
 * snapshot it is the first instant of the following month.
 */
export interface StatsPeriod {
  month: number; // 1-12
  monthStart: number;
  yearStart: number;
  end: number;
}

const MONITOR_STATUS_LIVE = 'live';

/**
 * Effective monitor status for a domain, mirroring the Report Builder:
 * not in zone wins, then an active override, then the 7-day high-water grace.
 */
export function resolveEffectiveStatus(
  d: {
    currentStatus: string;
    inCurrentZoneFile: boolean;
    graceExpiresAt?: number | null;
    highWaterStatus?: string | null;
  },
  overrideStatus: string | undefined,
  nowMs: number,
): string {
  if (!d.inCurrentZoneFile)
    return 'not_in_zone';
  if (overrideStatus)
    return overrideStatus;
  if (d.graceExpiresAt && d.graceExpiresAt > nowMs && d.highWaterStatus)
    return d.highWaterStatus;
  return d.currentStatus;
}

export function isEffectiveLive(status: string): boolean {
  return status === MONITOR_STATUS_LIVE;
}

export function computeLiveStats(
  domains: LiveDomainRow[],
  deals: StatsDeal[] | undefined,
  period: StatsPeriod,
): Record<string, string> {
  const { monthStart, yearStart, end, month: monthNumber } = period;
  const inWindow = (ts: number | null, start: number) => ts !== null && ts >= start && ts < end;

  const stats: Record<string, string> = {};

  // --- New domains (registrations) ---
  const registeredThisMonth = domains.filter((d) => inWindow(d.registrationDate, monthStart));
  const registeredThisYear = domains.filter((d) => inWindow(d.registrationDate, yearStart));
  stats.newDomainsMonth = String(registeredThisMonth.length);
  stats.newDomainsYtd = String(registeredThisYear.length);

  // New vs existing registrants (rough, by registrant email's earliest registration)
  const earliestByEmail = new Map<string, number>();
  for (const d of domains) {
    if (!d.registrantEmail || !d.registrationDate)
      continue;
    const email = d.registrantEmail.toLowerCase();
    const existing = earliestByEmail.get(email);
    if (existing === undefined || d.registrationDate < existing) {
      earliestByEmail.set(email, d.registrationDate);
    }
  }
  let newRegistrants = 0;
  let existingRegistrants = 0;
  for (const d of registeredThisYear) {
    const email = d.registrantEmail?.toLowerCase();
    const earliest = email ? earliestByEmail.get(email) : undefined;
    if (earliest !== undefined && earliest < yearStart) {
      existingRegistrants++;
    } else {
      newRegistrants++;
    }
  }
  stats.newDomainsNewRegistrants = String(newRegistrants);
  stats.newDomainsExistingRegistrants = String(existingRegistrants);

  // --- Activations (live) ---
  const wentLiveThisMonth = domains.filter((d) => inWindow(d.liveSince, monthStart));
  const wentLiveThisYear = domains.filter((d) => inWindow(d.liveSince, yearStart));
  stats.newActivationsMonth = String(wentLiveThisMonth.length);
  stats.newActivationsYtd = String(wentLiveThisYear.length);
  stats.avgMonthlyNewActivations = (wentLiveThisYear.length / monthNumber).toFixed(2);

  const liveNow = domains.filter((d) => d.effectiveLive);
  // Unknown region counts as US to keep US + International = Total
  const liveUs = liveNow.filter((d) => d.registrantRegion === 'US' || d.registrantRegion === null);
  stats.activationsUs = String(liveUs.length);
  stats.activationsIntl = String(liveNow.length - liveUs.length);
  stats.activationsTotal = String(liveNow.length);

  // --- Registrants & DUM ---
  stats.dum = String(domains.length);

  const registrantEmails = new Set<string>();
  const registrantEmailsUs = new Set<string>();
  for (const d of domains) {
    if (!d.registrantEmail)
      continue;
    const email = d.registrantEmail.toLowerCase();
    registrantEmails.add(email);
    if (d.registrantRegion === 'US' || d.registrantRegion === null) {
      registrantEmailsUs.add(email);
    }
  }
  // A registrant with any US-region domain counts as US
  stats.uniqueRegistrants = String(registrantEmails.size);
  stats.uniqueRegistrantsUs = String(registrantEmailsUs.size);
  stats.uniqueRegistrantsIntl = String(registrantEmails.size - registrantEmailsUs.size);

  // --- Applications (New Business deals with a .bank domain) ---
  if (deals) {
    const bankNewBusiness = deals.filter(
      (deal) =>
        deal.deal_type === 'New Business'
        && deal.domains.some((d) => d.toLowerCase().endsWith('.bank')),
    );
    const createdAt = (deal: StatsDeal) => new Date(deal.created_at).getTime();
    const createdThisMonth = bankNewBusiness.filter((d) => inWindow(createdAt(d), monthStart));
    const createdThisYear = bankNewBusiness.filter((d) => inWindow(createdAt(d), yearStart));
    stats.applicationsMonth = String(createdThisMonth.length);
    stats.applicationsYtd = String(createdThisYear.length);

    const decided = createdThisYear.filter((d) => d.outcome !== null);
    const approved = decided.filter((d) => d.outcome === 'Approved');
    if (decided.length > 0) {
      stats.approvalRate = `${((approved.length / decided.length) * 100).toFixed(2)}%`;
    }
  }

  return stats;
}
